import z from 'zod';

const US_COUNTRY_ISO_CD = 'US';

export const isUS = (countryIsoCd: string): boolean => {
  return countryIsoCd === US_COUNTRY_ISO_CD;
};

export const reduceAddress = (address: Partial<IAddress>): IAddress => {
  const base = {
    streetAddr1: address.streetAddr1 || '',
    streetAddr2: address.streetAddr2,
    streetAddr3: address.streetAddr3,
    countryIsoCd: address.countryIsoCd || '',
    city: address.city || '',
    label: address.label,
    contactName: address.contactName,
    contactPhone: address.contactPhone,
  };

  if (isUS(base.countryIsoCd)) {
    return {
      ...base,
      stateCd: address.stateCd,
      zip5: address.zip5,
      zip4: !!address.zip4 ? address.zip4 : undefined
    };
  } else {
    return {
      ...base,
      province: address.province,
      intlPostalCd: address.intlPostalCd,
    };
  }
};

export const AddressSchema = z.object({
  streetAddr1: z.string().min(1),
  streetAddr2: z.string().optional(),
  streetAddr3: z.string().optional(),
  countryIsoCd: z.string().length(2),
  city: z.string().min(1),
  stateCd: z.string().length(2),
  zip5: z.string().optional(),
  zip4: z.string().optional(),
  province: z.string().optional(),
  intlPostalCd: z.string().optional()
}).refine((data) => {
  if (isUS(data.countryIsoCd)) {
    return !!data.stateCd && !!data.zip5;
  } else {
    return !!data.province && !!data.intlPostalCd;
  }
}, "Missing regional data");


const AddressSchemaBase = z.object({
  streetAddr1: z.string().min(3),
  streetAddr2: z.string().optional(),
  streetAddr3: z.string().optional(),
  countryIsoCd: z.string().min(2).max(3),
  city: z.string().min(1),
  label: z.string().optional(),
  contactName: z.string().optional(),
  contactPhone: z.string().max(12).optional(),
});

const AddressUSSchema = AddressSchemaBase.extend({
  stateCd: z.string().min(2).max(3),
  zip5: z.string().length(5),
  zip4: z.union([z.string().length(4), z.undefined()])
});

const AddressIntlSchema = AddressSchemaBase.extend({
  province: z.string().min(2).optional(),
  intlPostalCd: z.string().min(1).optional()
});

export const getAddressSchema = (countryIsoCd: string | undefined) => {
  if (countryIsoCd) {
    return isUS(countryIsoCd) ? AddressUSSchema : AddressIntlSchema;
  }
  return AddressSchemaBase;
};

export interface IAddress {
  streetAddr1: string;
  streetAddr2?: string;
  streetAddr3?: string;
  countryIsoCd: string;
  city: string;

  province?: string;
  intlPostalCd?: string;
  stateCd?: string;
  zip5?: string;
  zip4?: string;
  label?: string;
  contactName?: string;
  contactPhone?: string;
}
